import * as RadioGroup from "@radix-ui/react-radio-group"
import clsx from "clsx"
import type { ReactNode } from "react"

import type { SX } from "../../../theme"
import { Icon, type IconName } from "../../Icon"
import { Text } from "../../Text"
import styles from "./DialogOptions.module.css"

export interface DialogOptionsProps {
  value?: string
  onValueChange?: (newValue: string) => void
  children: ReactNode
  sx?: SX
  /**
   * Determines the padding for the dialog options panel
   * @default medium
   */
  padding?: "small" | "medium"
  /**
   * Determines the width of the dialog options panel based on set of options.
   *
   * @param auto - Add auto width to the panel and let it grow with its content
   * @default large
   */
  width?: "large" | "auto"
  /**
   * The sticky footer of the dialog options panel.
   */
  footer?: ReactNode
}

export const DialogOptions = ({
  value,
  onValueChange,
  children,
  width = "large",
  padding = "medium",
  footer,
}: DialogOptionsProps) => (
  <aside className={styles.root}>
    <RadioGroup.Root
      className={clsx(
        styles.groupRoot,
        styles[`width-${width}`],
        styles[`padding-${padding}`],
      )}
      value={value}
      onValueChange={onValueChange}
      orientation="vertical"
    >
      {children}
    </RadioGroup.Root>
    {footer && (
      <div className={clsx(styles.footer, styles[`padding-${padding}`])}>
        {footer}
      </div>
    )}
  </aside>
)

interface DialogOptionsItemProps {
  value: string
  icon?: IconName
  title?: string
  subtitle?: string
}

export const DialogOptionsItem = ({
  value,
  icon,
  title,
  subtitle,
}: DialogOptionsItemProps) => {
  const hasContent = title || subtitle
  const itemClassName = clsx(
    styles.item,
    styles[`inlinePadding-${icon ? "icon" : "default"}`],
    !hasContent && styles.uniformPadding,
  )

  return (
    <RadioGroup.Item className={itemClassName} value={value}>
      {icon && (
        <div className={styles.itemIcon}>
          <Icon name={icon} size="medium" color="grey11" />
        </div>
      )}
      {hasContent && (
        <div className={styles.itemTextContainer}>
          <Text variant="bold" color="grey12" noWrap>
            {title}
          </Text>
          <Text variant="small" color="grey11" noWrap>
            {subtitle}
          </Text>
        </div>
      )}
    </RadioGroup.Item>
  )
}
